import { Badge } from "components/ui/badge";
import { Button } from "components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "components/ui/popover";
import { Plus, RotateCcw, X } from "lucide-react";
import { useMemo, useState } from "react";
import { Responsive, WidthProvider, type Layouts } from "react-grid-layout";
import { cn } from "@/lib/utils";
import {
  GRID_BREAKPOINTS,
  GRID_COLS,
  GRID_MARGIN,
  GRID_ROW_HEIGHT,
  HOME_BLOCK_MAP,
  type StoredHomeLayout,
  visibleIds,
  visibleLayouts,
} from "./home-blocks";
import { useHomeLayout } from "./home-layout-context";

// Measures its container's width and picks the matching breakpoint
const ResponsiveGridLayout = WidthProvider(Responsive);

// The whole tile is the drag surface in edit mode; the
// Card content is made inert so a drag never triggers navigation.
function BlockTile({ id, editing }: { id: string; editing: boolean }) {
  const { removeDraftBlock } = useHomeLayout();
  const def = HOME_BLOCK_MAP[id];
  if (!def) return null;

  return (
    <>
      {editing && (
        <Button
          variant="ghost"
          size="icon"
          className="home-no-drag absolute top-2 right-2 z-20 size-7 rounded-md border border-border bg-card/95 text-muted-foreground shadow-sm backdrop-blur hover:text-destructive"
          title="Remove block"
          data-testid={`home-block-${id}-remove`}
          onClick={() => removeDraftBlock(id)}
        >
          <X className="size-4" />
        </Button>
      )}
      <div
        className={cn("h-full", editing && "pointer-events-none select-none")}
      >
        {def.render(editing)}
      </div>
    </>
  );
}

function AddBlockPalette() {
  const { draft, addDraftBlock } = useHomeLayout();
  const [open, setOpen] = useState(false);
  const hidden = draft.hidden.filter((id) => HOME_BLOCK_MAP[id]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" data-testid="home-add-block">
          <Plus className="size-4 mr-2" />
          Add block
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-2">
        {hidden.length === 0 ? (
          <p className="text-sm text-muted-foreground p-3 text-center">
            All blocks are already on your home page.
          </p>
        ) : (
          <div className="space-y-1">
            {hidden.map((id) => {
              const def = HOME_BLOCK_MAP[id];
              return (
                <button
                  key={id}
                  type="button"
                  data-testid={`home-add-block-${id}`}
                  onClick={() => {
                    addDraftBlock(id);
                    setOpen(false);
                  }}
                  className="flex w-full flex-col items-start gap-0.5 rounded-md p-2 text-left transition-colors hover:bg-accent"
                >
                  <span className="text-sm font-medium">{def.title}</span>
                  <span className="text-xs text-muted-foreground">
                    {def.description}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

function Grid({
  source,
  editing,
}: {
  source: StoredHomeLayout;
  editing: boolean;
}) {
  const { setLayouts } = useHomeLayout();
  const ids = visibleIds(source);
  const layouts: Layouts = useMemo(() => visibleLayouts(source), [source]);

  return (
    <ResponsiveGridLayout
      // RGL forwards className (not arbitrary data-*) to its container div, so
      // `home-grid` is the stable hook for styling and e2e selectors.
      className="home-grid"
      layouts={layouts}
      breakpoints={GRID_BREAKPOINTS}
      cols={GRID_COLS}
      rowHeight={GRID_ROW_HEIGHT}
      margin={GRID_MARGIN}
      // Cards sit flush with the page's own padding; RGL would otherwise add a
      // margin-sized ring inside the container, indenting the grid past the title.
      containerPadding={[0, 0]}
      isDraggable={editing}
      isResizable={editing}
      isBounded
      draggableCancel=".home-no-drag"
      onLayoutChange={(_current, all) => {
        if (editing) setLayouts(all);
      }}
    >
      {ids.map((id) => (
        <div
          key={id}
          data-block-id={id}
          data-testid={`home-block-${id}`}
          className={cn(
            "h-full rounded-lg",
            editing && "ring-1 ring-border transition-shadow hover:shadow-sm",
          )}
        >
          <BlockTile id={id} editing={editing} />
        </div>
      ))}
    </ResponsiveGridLayout>
  );
}

function EmptyCanvas({ editing }: { editing: boolean }) {
  return (
    <div
      data-testid="home-empty"
      className="relative flex min-h-70 items-center justify-center px-10 text-center"
    >
      <p className="text-sm">
        <span className="font-semibold">It&apos;s lonely here.</span>{" "}
        <span className="text-muted-foreground">
          {editing
            ? "Add a block to put your cards back."
            : "Edit layout to put your cards back."}
        </span>
      </p>
      <svg
        viewBox="0 0 140 120"
        aria-hidden="true"
        data-testid="home-empty-arrow"
        className={cn(
          "home-empty-arrow pointer-events-none absolute hidden h-30 w-35 fill-none stroke-primary stroke-2 sm:block",
          editing ? "-top-9 right-38" : "-top-4 right-6",
        )}
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M10 110 C 54 112, 108 106, 122 16 M112 30 L122 16 L133 27" />
      </svg>
    </div>
  );
}

export function HomeGrid() {
  const { layout, draft, isEditing, reset } = useHomeLayout();

  if (!isEditing) {
    return visibleIds(layout).length === 0 ? (
      <EmptyCanvas editing={false} />
    ) : (
      <Grid source={layout} editing={false} />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-dashed border-border bg-muted/30 p-3">
        <Badge variant="secondary">Editing layout</Badge>
        <span className="text-sm text-muted-foreground">
          Drag a card to move it, drag its bottom-right corner to resize. Remove
          or add blocks, then Save.
        </span>
        <div className="ml-auto flex items-center gap-2">
          <AddBlockPalette />
          <Button
            variant="ghost"
            size="sm"
            onClick={reset}
            data-testid="home-reset"
          >
            <RotateCcw className="size-4 mr-2" />
            Reset to default
          </Button>
        </div>
      </div>

      {visibleIds(draft).length === 0 ? (
        <EmptyCanvas editing />
      ) : (
        <Grid source={draft} editing />
      )}
    </div>
  );
}
